
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, TrendingUp, TrendingDown, Bell, MapPin } from 'lucide-react';
import { PriceHistoryChart } from './PriceHistoryChart';
import { publicFeatures } from '../config/publicFeatures';
import { Badge } from './ui/badge';
import { PriceDisplay } from './ui/PriceDisplay';
import { Link } from 'react-router-dom';

export function ProductCard({ product }) {
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!product) return null;

  const {
    title,
    brand,
    image_url,
    rating,
    review_count,
    price,
    price_old,
    discount,
    stock_status,
    asin,
    is_prime,
    price_history,
    updated_at
  } = product;

  // Get partner tag from environment
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;

  // Build Amazon URL with partner tag
  const amazonUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;

  // Calculate estimated delivery date (5 days from now)
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 5);
  const formattedDeliveryDate = deliveryDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  // Check if price history is available
  const hasPriceHistory = publicFeatures.priceHistory && Array.isArray(price_history) && price_history.length > 0;

  return (
    <motion.div 
      layout="position"
      className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden border border-gray-200"
    >
      {/* Discount Badge */}
      {discount > 0 && (
        <div className="absolute top-2 left-2 z-10">
          <Badge className="bg-amazon-red text-white font-bold px-2 py-1">
            {Math.round(discount)}% off
          </Badge>
        </div>
      )}

      {/* Prime Badge */}
      {is_prime && (
        <div className="absolute top-2 right-2 z-10">
          <Badge className="bg-amazon-blue text-white px-2 py-1">
            Prime
          </Badge>
        </div>
      )}

      {/* Product Image */}
      <Link to={`/dp/${asin}`} className="block">
        <div className="relative aspect-square flex justify-center items-center bg-white p-4">
          {!isImageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="w-8 h-8 border-2 border-amazon-orange border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {image_url ? (
            <img
              src={image_url}
              alt={title}
              className={`w-auto h-auto object-contain transition-opacity duration-300 ${
                isImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setIsImageLoaded(true);
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400">No image available</span>
            </div>
          )}
        </div>
      </Link>

      {/* Product Info */}
      <div className="p-4 space-y-3">
        {/* Title & Brand */}
        <div>
          <Link to={`/dp/${asin}`}>
            <h3 className="text-sm font-medium line-clamp-2 text-amazon-text-primary hover:text-amazon-link-hover cursor-pointer dark:text-gray-400 transition-colors">
              {title}
            </h3>
          </Link>
          {brand && (
            <p className="text-xs text-amazon-text-secondary inline-flex items-center rounded-full border text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-primary/80 bg-amazon-red text-white font-bold px-2 py-1 mt-2">{brand}</p>
          )}
        </div>

        {/* TechieDay Score */}
        {rating > 0 && (
          <div className="mb-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${(rating * 2 / 10) * 100}%`,
                    backgroundColor: rating * 2 <= 3 ? '#EF4444' : 
                                   rating * 2 <= 6 ? '#F97316' : 
                                   rating * 2 <= 8 ? '#22C55E' : '#3B82F6'
                  }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[2.5rem]">
                {(rating * 2).toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-amazon-text-primary mt-1">
              {review_count > 999 ? `${(review_count/1000).toFixed(1)}k` : review_count} reviews
            </div>
          </div>
        )}

        {/* Price with Policy-Compliant Display */}
        <div className="space-y-1">
          <PriceDisplay
            price={price}
            priceOld={price_old}
            lastUpdated={updated_at}
            stockStatus={stock_status}
            asin={asin}
            size="normal"
            showWarning={true}
          />
          
          {/* Delivery Info */}
          <div className="text-xs text-amazon-text-secondary space-y-1">
            {is_prime ? (
              <>
                <p className="text-amazon-text-primary">FREE Prime Delivery</p>
                <p>Get it by <span className="font-bold">{formattedDeliveryDate}</span></p>
              </>
            ) : (
              <p>
                FREE delivery: <span className="font-bold">{formattedDeliveryDate}</span>
              </p>
            )}
            <div className="flex items-center gap-1">
              <MapPin size={14} />
              <span>Deliver to Los Angeles 90022</span>
            </div>
          </div>
        </div>

        {/* Stock Status */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              stock_status 
                ? 'text-amazon-green'
                : 'text-amazon-red'
            }`}>
              {stock_status ? 'In Stock' : 'Out of Stock'}
            </span>
            {!stock_status && publicFeatures.priceAlerts && (
              <button 
                className="text-xs text-amazon-link flex items-center gap-1 hover:text-amazon-link-hover hover:underline"
                onClick={() => {/* TODO: Implement alert functionality */}}
              >
                <Bell size={14} />
                Alert Me
              </button>
            )}
          </div>
          
          {stock_status && stock_status < 20 && (
            <p className="text-xs text-amazon-red">
              Only {stock_status} left in stock - order soon
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 pt-2">
          <a
            href={amazonUrl}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            className="flex items-center justify-center gap-2 w-full bg-amazon-yellow hover:bg-amazon-yellow-hover text-amazon-text-primary px-4 py-2 rounded-full font-medium text-sm transition-colors shadow-sm hover:shadow-md"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48" fill="currentColor">
              <path d="M26.55,33.46c-2.49,1.84-6.1,2.82-9.21,2.82c-4.36,0-8.28-1.61-11.25-4.3c-0.23-0.21-0.02-0.5,0.25-0.34 c3.16,1.84,7.07,2.95,11.11,2.95c2.72,0,5.71-0.56,8.46-1.73C26.32,32.66,26.93,33.15,26.55,33.46z M27.63,32.24 c-0.32-0.41-2.11-0.19-2.91-0.1c-0.24,0.03-0.28-0.18-0.06-0.33c1.43-1,3.77-0.71,4.04-0.38c0.27,0.33-0.07,2.64-1.39,3.74 c-0.2,0.17-0.39,0.08-0.3-0.14C27.29,34.32,27.95,32.65,27.63,32.24z"/>
            </svg>
            View on Amazon
            <ExternalLink size={16} />
          </a>
          
          {publicFeatures.priceHistory && (
          <button
            onClick={() => setShowPriceHistory(true)}
            disabled={!hasPriceHistory}
            className={`w-full border border-[#D5D9D9] px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              hasPriceHistory
                ? 'bg-white hover:border-amazon-link text-amazon-link'
                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            <TrendingDown size={16} />
            Price History
          </button>
          )}
        </div>
      </div>

      {/* Price History Modal */}
      {showPriceHistory && hasPriceHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Price History</h3>
              <button
                onClick={() => setShowPriceHistory(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <PriceHistoryChart asin={asin} />
          </div>
        </div>
      )}
    </motion.div>
  );
}
